import React from 'react'
import FormatCurrency from 'components/Common/FormatCurrency'
import Caption from 'LuxKit/Typography/Caption'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLink from 'LuxKit/TextLink'
import type { CalendarPickerMonthView } from 'selectors/offerPage/offerPageSelectors'
import MobileMultipleRoomsDurationLabel from 'components/OfferPage/MultipleRoomsBooking/Mobile/MobileMultipleRoomsDurationLabel'
import { useIntl } from 'react-intl'

interface Props {
  duration: number
  month: CalendarPickerMonthView
  hasOtherAvailability: boolean
  enableCalendarRecommendations: boolean
  selected?: boolean
  soldOutText?: string
  hideChildPrices?: boolean
  markCheapestPrice?: boolean
  isMultiRoom?: boolean
}

function OfferCalendarHotelMonthTile(props: Props) {
  const {
    month,
    duration,
    hasOtherAvailability,
    enableCalendarRecommendations,
    selected,
    soldOutText = 'Sold out',
    hideChildPrices,
    markCheapestPrice,
    isMultiRoom,
  } = props

  const intl = useIntl()
  const subTextColour = selected ? undefined : 'neutral-three'
  const priceDefaultColour = markCheapestPrice && month.cheapest ? 'highlight-secondary' : undefined
  const priceColour = month.isLuxPlusPrice && !selected ? 'lux-plus' : priceDefaultColour

  const defaultSoldOutText = intl.formatMessage({
    id: 'a9/J1XfErN',
    defaultMessage: 'Sold out',
    description: 'Text shown when a month is sold out in calendar month picker',
  })

  const viewSimilarOffersText = intl.formatMessage({
    id: 'os5Oyb/wcz',
    defaultMessage: 'View similar offers',
    description: 'Link text to view similar offers when a month is sold out in calendar month picker',
  })

  const availableInOtherRoomTypesText = intl.formatMessage({
    id: 'o2gwO6EDNy',
    defaultMessage: 'Available in other room types',
    description: 'Link text shown when room is sold out but other room types are available in month tile',
  })
  const selectCheckInDateText = intl.formatMessage({
    id: 'nST/Qwkzm7',
    defaultMessage: 'Select check-in date',
    description: 'Label to select check-in date when child prices are hidden in month tile',
  })
  const viewAvailabilityText = intl.formatMessage({
    id: 'njgWBcnPd/',
    defaultMessage: 'View availability',
    description: 'Label to view availability when check-in is selected in month tile',
  })

  return (
    <div>
      {!month.hotelAvailable && <>
        <Caption variant="medium" colour={subTextColour}>{soldOutText === 'Sold out' ? defaultSoldOutText : soldOutText}</Caption>
        {!hasOtherAvailability && enableCalendarRecommendations && <TextLink size="small" weight="regular">
          {viewSimilarOffersText}
        </TextLink>}
        {hasOtherAvailability &&
          <TextLink size="small" weight="regular">
            {availableInOtherRoomTypesText}
          </TextLink>}
      </>}
      {month.hotelAvailable && <>
        {hideChildPrices && <Caption variant="medium" colour="neutral-three" underline>{!selected ? selectCheckInDateText : viewAvailabilityText}</Caption>}
        {!hideChildPrices && <div style={{ display: 'inline-flex', gap: '0.25em', alignItems: 'baseline', flexWrap: 'wrap' }}>
          {!isMultiRoom && <Caption variant="medium" colour={subTextColour}>{intl.formatMessage({
            id: 'eFQuLYehOt',
            defaultMessage: '{nights, plural, one {# night} other {# nights}} from',
            description: 'Night count with from label for hotel calendar month tile',
          }, { nights: duration })}
          </Caption>}
          {isMultiRoom && <Caption variant="medium" colour={subTextColour}><MobileMultipleRoomsDurationLabel shouldDisplayRoom shouldDisplayFlight={false} /></Caption>}
          <BodyText as="span" variant="medium" weight="bold" colour={priceColour}>
            <FormatCurrency value={month.hotelTotal} />
          </BodyText>
          <Caption as="span" variant="medium" colour={(month.isLuxPlusPrice && !selected) ? 'lux-plus' : subTextColour}>
            {intl.formatMessage({
              id: 'wsU/CoTbWq',
              defaultMessage: 'total',
              description: 'Total price label for hotel calendar month tile',
            })}
          </Caption>
        </div>}
      </> }
    </div>
  )
}

export default React.memo(OfferCalendarHotelMonthTile)
